import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Modal, Form, Row, Col, Button } from 'react-bootstrap';
import styles from './Musteriler.module.css';
import TrDateInput from '../components/TrDateInput';
import { useTranslation } from 'react-i18next';

const maskPhone = (phone: string): string => {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 10) {
        return `(0${numbers.slice(0, 3)}) *** ** ${numbers.slice(8)}`;
    }
    return phone.replace(/\d(?=\d{2})/g, '*');
};

const formatPhoneNumber = (phone: string): string => {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 10) {
        return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8)}`;
    }
    return phone;
};

const getInitials = (name: string): string =>
    name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

const getAvatarStyle = (name: string): React.CSSProperties => {
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const palettes = [
        { background: '#fff8f0', color: '#92400e' },
        { background: '#f1f5f9', color: '#334155' },
        { background: '#fef3c7', color: '#78350f' },
        { background: '#ede9fe', color: '#4c1d95' },
        { background: '#e0f2fe', color: '#0c4a6e' },
    ];
    const p = palettes[hash % palettes.length];
    return { background: p.background, color: p.color, fontWeight: 700 };
};

interface Cari {
    cariID: number;
    takipNo: string;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    kaynak: string | null;
    aktifMi: boolean;
    eklenmeTarihi: string;
}

interface CariForm {
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    kaynak: string | null;
    aktifMi: boolean;
}

interface Sozlesme {
    sozlesmeID: number;
    sozlesmeDurumu: string;
    sozlesmeFiyati: number;
    daire: {
        daireID: number;
        daireNo: string;
        blok?: {
            blokAdi: string;
            proje?: {
                projeAdi: string;
            };
        };
    };
}

const durumBadgeColors: Record<string, { bg: string; color: string; border: string }> = {
    'Sözleşme':   { bg: '#fff8f0', color: '#92400e', border: '#fed7aa' },
    'Rezerve':    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
    'ArsaSahibi': { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
    'Taşeron':    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
    'Aktif':      { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
};

const Musteriler: React.FC = () => {
    const [musteriler, setMüşteriler] = useState<Cari[]>([]);
    const [filteredMüşteriler, setFilteredMüşteriler] = useState<Cari[]>([]);
    const [filters, setFilters] = useState({ musteriAdi: '', telefon: '', durum: 'aktif', proje: 'all', odemeDurumu: 'all', tarihBaslangic: '', tarihBitis: '', altKategori: 'all' });
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editCari, setEditCari] = useState<Cari | null>(null);
    const [sozlesmeMap, setSozlesmeMap] = useState<Record<number, Sozlesme[]>>({});
    const [odemeMap, setOdemeMap] = useState<Record<number, 'green' | 'yellow' | 'red'>>({});
    const [bakiyeMap, setBakiyeMap] = useState<Record<number, number>>({});
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [formData, setFormData] = useState<CariForm>({
        adiSoyadi: '', firmaAdi: null, telefon1: '', telefon2: null,
        ePosta: null, adres: null, tc: null, cariVD: null, cariVN: null,
        turu: 'Müşteri', kaynak: null, aktifMi: true
    });
    const [altKategoriler, setAltKategoriler] = useState<{cariAltKategoriID: number; altKategoriAdi: string}[]>([]);
    const [leadKaynaklar, setLeadKaynaklar] = useState<{ id: number; ad: string; aktifMi: boolean }[]>([]);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleEditClick = async (e: React.MouseEvent, cariID: number) => {
        e.stopPropagation();
        try {
            const response = await axios.get(`/api/Cariler/${cariID}`);
            const cari = response.data;
            setEditCari(cari);
            setFormData({
                adiSoyadi: cari.adiSoyadi,
                firmaAdi: cari.firmaAdi,
                telefon1: cari.telefon1,
                telefon2: cari.telefon2,
                ePosta: cari.ePosta,
                adres: cari.adres,
                tc: cari.tc,
                cariVD: cari.cariVD,
                cariVN: cari.cariVN,
                turu: cari.turu,
                kaynak: cari.kaynak,
                aktifMi: cari.aktifMi
            });
            setShowEditModal(true);
        } catch {
            toast.error(t('msg.musteriYuklenemedi'));
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editCari) return;
        try {
            await axios.put(`/api/Cariler/${editCari.cariID}`, formData);
            toast.success(t('msg.musteriGuncellendi'));
            setShowEditModal(false);
            fetchMüşteriler();
        } catch {
            toast.error(t('msg.guncellemeSirasindaHata'));
        }
    };

    const fetchMüşteriler = async () => {
        try {
            const response = await axios.get('/api/Cariler');
            setMüşteriler(response.data);
            setFilteredMüşteriler(response.data);

            // Sözleşmeleri çek
            try {
                const sozRes = await axios.get('/api/Sozlesmeler?aktifMi=true');
                const map: Record<number, Sozlesme[]> = {};
                for (const s of sozRes.data) {
                    const cariID = s.cari?.cariID;
                    if (cariID) {
                        if (!map[cariID]) map[cariID] = [];
                        map[cariID].push(s);
                    }
                }
                setSozlesmeMap(map);
            } catch {
                // sözleşme hatası listelemeyi engellemesin
            }

            // Cari bakiyeleri çek
            try {
                const bakiyeRes = await axios.get('/api/Cariler/musteri-net-bakiyeler');
                const bMap: Record<number, number> = {};
                for (const b of bakiyeRes.data) {
                    bMap[b.cariID] = b.netBakiye;
                }
                setBakiyeMap(bMap);
            } catch {
                // bakiye hatası listelemeyi engellemesin
            }

            // Ödemeleri çek – geciken ödeme indikatörü için
            try {
                const odemeRes = await axios.get('/api/Odemeler?aktifMi=true');
                const today = new Date();
                const statusMap: Record<number, 'green' | 'yellow' | 'red'> = {};
                for (const o of odemeRes.data) {
                    const cid = o.cari?.cariID || o.cariID;
                    if (!cid) continue;
                    if (o.odemeDurumu !== 'Bekliyor') continue;
                    // Sadece müşterinin bize ödeyeceği tahsilatlar (Tahsilat yönlü)
                    if (o.odemeYonu !== 'Tahsilat') continue;
                    // GEL-SOZ-* kayıtları sözleşme toplam tutarıdır, gerçek taksit değildir → gecikme kontrolüne dahil etme
                    if (o.islemTakipNo?.startsWith('GEL-SOZ-')) continue;
                    // vadeTarihi null ise atlıyoruz (new Date(null) = 1970 → yanlış pozitif)
                    if (!o.vadeTarihi) continue;
                    const vade = new Date(o.vadeTarihi);
                    const diffDays = Math.floor((vade.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    let status: 'green' | 'yellow' | 'red' = 'green';
                    if (diffDays < 0) status = 'red';
                    else if (diffDays <= 7) status = 'yellow';
                    const prev = statusMap[cid];
                    if (!prev || status === 'red' || (status === 'yellow' && prev === 'green')) {
                        statusMap[cid] = status;
                    }
                }
                setOdemeMap(statusMap);
            } catch {
                // ödeme hatası listelemeyi engellemesin
            }
        } catch {
            toast.error(t('msg.musteriYuklemeHata'));
        }
    };

    useEffect(() => {
        fetchMüşteriler();
        fetchAltKategoriler();
        axios.get('/api/LeadKaynaklar').then((r: any) => setLeadKaynaklar(r.data)).catch(() => {});
    }, []); // eslint-disable-line

    const fetchAltKategoriler = async () => {
        try {
            const response = await axios.get('/api/CariAltKategorileri/aktif?turu=Müşteri');
            setAltKategoriler(response.data);
        } catch { /* sessiz */ }
    };

    useEffect(() => {
        let filtered = musteriler.filter(m => m.turu === 'Müşteri');
        if (filters.musteriAdi.trim()) {
            const q = filters.musteriAdi.toLowerCase().trim();
            filtered = filtered.filter(m =>
                m.adiSoyadi.toLowerCase().includes(q) ||
                (m.firmaAdi && m.firmaAdi.toLowerCase().includes(q)) ||
                m.telefon1.includes(q)
            );
        }
        if (filters.telefon.trim()) {
            const nums = filters.telefon.replace(/\D/g, '');
            filtered = filtered.filter(m => m.telefon1.replace(/\D/g, '').includes(nums));
        }
        if (filters.durum !== 'all') {
            filtered = filtered.filter(m => filters.durum === 'aktif' ? m.aktifMi : !m.aktifMi);
        }
        if (filters.proje !== 'all') {
            filtered = filtered.filter(m => {
                const sozlesmeler = sozlesmeMap[m.cariID];
                if (!sozlesmeler) return false;
                return sozlesmeler.some(s => s.daire?.blok?.proje?.projeAdi === filters.proje);
            });
        }
        if (filters.odemeDurumu !== 'all') {
            filtered = filtered.filter(m => {
                const status = odemeMap[m.cariID];
                if (filters.odemeDurumu === 'geciken') return status === 'red';
                if (filters.odemeDurumu === 'yaklasan') return status === 'yellow';
                if (filters.odemeDurumu === 'normal') return status === 'green';
                if (filters.odemeDurumu === 'yok') return !status;
                return true;
            });
        }
        if (filters.tarihBaslangic) {
            const start = new Date(filters.tarihBaslangic);
            filtered = filtered.filter(m => new Date(m.eklenmeTarihi) >= start);
        }
        if (filters.tarihBitis) {
            const end = new Date(filters.tarihBitis);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(m => new Date(m.eklenmeTarihi) <= end);
        }
        if (filters.altKategori !== 'all') {
            filtered = filtered.filter(m => m.turu2 === filters.altKategori);
        }
        setFilteredMüşteriler(filtered);
        setCurrentPage(1);
    }, [filters, musteriler, sozlesmeMap, odemeMap]);

    const handleFilterChange = (key: string, value: string) =>
        setFilters(prev => ({ ...prev, [key]: value }));

    const clearFilters = () => { setFilters({ musteriAdi: '', telefon: '', durum: 'aktif', proje: 'all', odemeDurumu: 'all', tarihBaslangic: '', tarihBitis: '', altKategori: 'all' }); setCurrentPage(1); };

    const totalPages = Math.ceil(filteredMüşteriler.length / pageSize);
    const paginatedItems = filteredMüşteriler.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // ── Summary metrics ──
    const allMusteri = musteriler.filter(m => m.turu === 'Müşteri');
    const toplamMusteriSayisi = allMusteri.length;
    const aktifSozlesmeSayisi = allMusteri.filter(m => sozlesmeMap[m.cariID]?.some(s => s.sozlesmeDurumu === 'Sözleşme')).length;
    const rezerveMusteri = allMusteri.filter(m => sozlesmeMap[m.cariID]?.some(s => s.sozlesmeDurumu === 'Rezerve')).length;
    const odemeRiskSayisi = allMusteri.filter(m => odemeMap[m.cariID] === 'red').length;
    const toplamPortfoyDegeri = allMusteri.flatMap(m => sozlesmeMap[m.cariID] || []).reduce((sum, s) => sum + (s.sozlesmeFiyati || 0), 0);

    // Aktif filtre sayısı (detay filtreler)
    const activeFilterCount = [
        filters.durum !== 'aktif' ? 1 : 0,
        filters.proje !== 'all' ? 1 : 0,
        filters.odemeDurumu !== 'all' ? 1 : 0,
        filters.altKategori !== 'all' ? 1 : 0,
        filters.tarihBaslangic ? 1 : 0,
        filters.tarihBitis ? 1 : 0,
        filters.telefon ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    // Proje listesini sözleşmelerden çıkar
    const projeListesi = Array.from(new Set(
        Object.values(sozlesmeMap).flat()
            .map(s => s.daire?.blok?.proje?.projeAdi)
            .filter(Boolean)
    )).sort() as string[];

    return (
        <div className={styles.page}>
            <div className={styles.topBar}>
                <div>
                    <div className={styles.titleRow}>
                        <h1 className={styles.pageTitle}>{t('musteri.baslik')}</h1>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#64748b', margin: '4px 0 0' }}>{t('musteri.aciklama')}</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                        onClick={() => navigate('/musteriler-raporu')}
                        style={{ padding: '9px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        <i className="fas fa-chart-bar" style={{ fontSize: 12, color: '#64748b' }}></i>
                        {t('btn.rapor')}
                    </button>
                    <button className={styles.newBtn} onClick={() => navigate('/musteriler/yeni')}>
                        <i className="fas fa-plus" style={{ fontSize: 11 }}></i>
                        {t('musteri.yeni')}
                    </button>
                </div>
            </div>

            {/* ── Summary Cards ── */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {/* Toplam Müşteri */}
                <div style={{ flex: 1, minWidth: 150, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('musteri.toplam')}</span>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cd6906" width="17" height="17"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
                        </div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1 }}>{toplamMusteriSayisi}</div>
                    <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                        <span style={{ color: '#cd6906', fontWeight: 700 }}>{aktifSozlesmeSayisi}</span> {t('musteri.aktifSozlesme')}
                    </div>
                </div>

                {/* Rezerve Daireler */}
                <div style={{ flex: 1, minWidth: 150, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('musteri.rezerveDaireler')}</span>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#16a34a" width="17" height="17"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>
                        </div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1 }}>{rezerveMusteri}</div>
                    <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{t('musteri.potansiyelFirsat')}</div>
                </div>

                {/* Ödeme Riski */}
                <div style={{ flex: 1, minWidth: 150, background: odemeRiskSayisi > 0 ? '#fef2f2' : '#fff', borderRadius: 12, border: odemeRiskSayisi > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: odemeRiskSayisi > 0 ? '#b91c1c' : '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('musteri.odemeRiski')}</span>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#dc2626" width="17" height="17"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                        </div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: odemeRiskSayisi > 0 ? '#dc2626' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1 }}>{odemeRiskSayisi}</div>
                    <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{t('musteri.odemeGecikenler')}</div>
                </div>

                {/* Toplam Portföy */}
                <div style={{ flex: 1, minWidth: 150, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('musteri.toplamPortfoy')}</span>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#2563eb" width="17" height="17"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>
                        </div>
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1 }}>
                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(toplamPortfoyDegeri)}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{t('musteri.aktifSozlesmeTutari')}</div>
                </div>
            </div>

            {/* ── Quick Bar ── */}
            <div className={styles.quickBar}>
                <div className={styles.quickSearchWrap}>
                    <i className={"fas fa-search " + styles.quickSearchIcon}></i>
                    <input
                        type="text"
                        placeholder={t('label.musteriAra')}
                        value={filters.musteriAdi}
                        onChange={e => handleFilterChange('musteriAdi', e.target.value)}
                        className={styles.quickInput}
                    />
                </div>
                <div className={styles.quickBarRight}>
                    <span className="text-sm text-slate-400 whitespace-nowrap">{filteredMüşteriler.length} / {musteriler.filter(m => m.turu === 'Müşteri').length} kayıt</span>
                    <button
                        className={styles.filtersToggleBtn + (filterPanelOpen ? ' ' + styles.filtersToggleBtnActive : '')}
                        onClick={() => setFilterPanelOpen(o => !o)}
                        style={filterPanelOpen ? {} : { borderColor: activeFilterCount > 0 ? '#cd6906' : undefined }}
                    >
                        <i className="fas fa-sliders-h" style={{ fontSize: 11 }}></i>
                        {t('label.filtreler')}
                        {activeFilterCount > 0 && (
                            <span className={styles.activeFilterBadge}>{activeFilterCount}</span>
                        )}
                        <i className={"fas fa-chevron-" + (filterPanelOpen ? 'up' : 'down')} style={{ fontSize: 9, marginLeft: 2 }}></i>
                    </button>
                </div>
            </div>

            {/* ── Accordion Panel ── */}
            <div className={styles.filterPanel + (filterPanelOpen ? ' ' + styles.filterPanelOpen : '')}>
                <div className={styles.filterPanelInner}>
                    <div className={styles.filterPanelGrid}>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>{t('label.telefon')}</label>
                            <div className={styles.inputWrap}>
                                <i className={"fas fa-phone " + styles.inputIcon}></i>
                                <input type="text" placeholder={t('label.telefonAra')} value={filters.telefon}
                                    onChange={e => handleFilterChange('telefon', e.target.value)}
                                    className={styles.filterInput} />
                            </div>
                        </div>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>{t('label.durum')}</label>
                            <select value={filters.durum}
                                onChange={e => handleFilterChange('durum', e.target.value)}
                                className={styles.filterSelect}>
                                <option value="all">{t('durum.tumu')}</option>
                                <option value="aktif">{t('durum.aktif')}</option>
                                <option value="pasif">{t('durum.pasif')}</option>
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>{t('label.proje')}</label>
                            <select value={filters.proje}
                                onChange={e => handleFilterChange('proje', e.target.value)}
                                className={styles.filterSelect}>
                                <option value="all">{t('durum.tumu')}</option>
                                {projeListesi.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>{t('label.odemeDurumu')}</label>
                            <select value={filters.odemeDurumu}
                                onChange={e => handleFilterChange('odemeDurumu', e.target.value)}
                                className={styles.filterSelect}>
                                <option value="all">{t('durum.tumu')}</option>
                                <option value="geciken">{t('durum.geciken')}</option>
                                <option value="yaklasan">{t('durum.yaklasan')}</option>
                                <option value="normal">{t('durum.normal')}</option>
                                <option value="yok">{t('durum.odemeYok')}</option>
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>{t('label.altKategori')}</label>
                            <select value={filters.altKategori}
                                onChange={e => handleFilterChange('altKategori', e.target.value)}
                                className={styles.filterSelect}>
                                <option value="all">{t('durum.tumu')}</option>
                                {altKategoriler.map(k => (
                                    <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>{t('label.kayitTarihi')}</label>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <TrDateInput value={filters.tarihBaslangic}
                                    onChange={e => handleFilterChange('tarihBaslangic', e.target.value)}
                                    className={styles.filterInput} style={{ fontSize: 11, padding: '0 4px' }} />
                                <TrDateInput value={filters.tarihBitis}
                                    onChange={e => handleFilterChange('tarihBitis', e.target.value)}
                                    className={styles.filterInput} style={{ fontSize: 11, padding: '0 4px' }} />
                            </div>
                        </div>
                    </div>
                    <div className={styles.filterPanelFooter}>
                        <button className={styles.clearBtnPanel} onClick={clearFilters}>
                            <i className="fas fa-times" style={{ fontSize: 10 }}></i> {t('btn.filtreleriTemizle')}
                        </button>
                    </div>
                </div>
            </div>
            <div className={styles.tableCard}>
                <table className={styles.tableEl}>
                    <thead>
                        <tr>
                            <th>{t('label.musteri')}</th>
                            <th>{t('label.telefon')}</th>
                            <th className={`${styles.thRight} ${styles.colTutar}`}>{t('odeme.durumToplam')}</th>
                            <th className={styles.thRight} style={{ minWidth: 150 }}>{t('label.cariBakiye')}</th>
                            <th className={styles.colOdeme} style={{ textAlign: 'center' }}>{t('odeme.odemeDurumu')}</th>
                            <th className={styles.colDurum}>{t('label.durum')}</th>
                            <th className={styles.colIslem} style={{ width: '60px', textAlign: 'center' }}>{t('label.islemler')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMüşteriler.length === 0 ? (
                            <tr className={styles.emptyRow}>
                                <td colSpan={7}>
                                    <i className={"fas fa-users " + styles.emptyIcon}></i>
                                    {musteriler.length === 0 ? t('msg.henuzMusteriYok') : t('msg.filtreUygunMusteriYok')}
                                </td>
                            </tr>
                        ) : paginatedItems.map(musteri => (
                            <tr key={musteri.cariID}
                                onClick={() => navigate("/musteriler/" + musteri.cariID)}
                                onMouseEnter={() => setHoveredRow(musteri.cariID)}
                                onMouseLeave={() => setHoveredRow(null)}
                                className="hover:bg-slate-50 transition-colors cursor-pointer"
                            >
                                <td>
                                    <div className={styles.avatarCell}>
                                        <div className={styles.avatar} style={getAvatarStyle(musteri.adiSoyadi)}>
                                            {getInitials(musteri.adiSoyadi)}
                                        </div>
                                        <div>
                                            <div className={styles.nameText}>{musteri.adiSoyadi}</div>
                                            {musteri.firmaAdi && <div className={styles.firmText}>{musteri.firmaAdi}</div>}
                                            {sozlesmeMap[musteri.cariID]?.length > 0 && (() => {
                                                const soz = sozlesmeMap[musteri.cariID];
                                                const first = soz[0];
                                                const rest = soz.length - 1;
                                                const proje = first.daire?.blok?.proje?.projeAdi || '';
                                                const daire = first.daire?.daireNo || '';
                                                const durum = first.sozlesmeDurumu;
                                                const durumLabel = durum === 'ArsaSahibi' ? 'Arsa Sahibi' : durum;
                                                const badge = durumBadgeColors[durum] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            fontSize: '11px', lineHeight: '1.2',
                                                            padding: '2px 6px', borderRadius: '6px',
                                                            background: '#f8fafc', color: '#475569',
                                                            border: '1px solid #e9edf2',
                                                        }}>
                                                            {proje && <span style={{ fontWeight: 600, color: '#151515' }}>{proje}</span>}
                                                            {proje && daire && <span style={{ color: '#d1d5db' }}>/</span>}
                                                            {daire && <span style={{ color: '#6b7280' }}>{daire}</span>}
                                                            <span style={{
                                                                fontSize: '10px', fontWeight: 600,
                                                                padding: '1px 6px', borderRadius: '4px',
                                                                background: badge.bg, color: badge.color,
                                                                border: `1px solid ${badge.border}`,
                                                            }}>{durumLabel}</span>
                                                        </span>
                                                        {rest > 0 && (
                                                            <span style={{
                                                                fontSize: '10px', fontWeight: 600, color: '#94a3b8',
                                                                padding: '2px 7px', borderRadius: '6px',
                                                                background: '#f1f5f9', border: '1px solid #e2e8f0',
                                                            }}>+{rest} Daire</span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        <span className={styles.phoneText}>{maskPhone(musteri.telefon1)}</span>
                                    </div>
                                </td>
                                <td className={styles.colTutar} style={{ textAlign: 'right' }}>
                                    {sozlesmeMap[musteri.cariID] && sozlesmeMap[musteri.cariID].length > 0 ? (
                                        <span style={{ fontWeight: 700, color: '#151515', fontSize: '13px', letterSpacing: '-0.01em' }}>
                                            {sozlesmeMap[musteri.cariID].reduce((sum, s) => sum + (s.sozlesmeFiyati || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                                        </span>
                                    ) : (
                                        <span style={{ color: '#d1d5db', fontSize: '13px' }}>—</span>
                                    )}
                                </td>
                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingRight: 12 }}>
                                    {(() => {
                                        const bakiye = bakiyeMap[musteri.cariID];
                                        if (bakiye === undefined) return <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>;
                                        const pozitif = bakiye > 0;
                                        const sifir = bakiye === 0;
                                        const renk = sifir ? '#94a3b8' : pozitif ? '#dc2626' : '#16a34a';
                                        const etiket = sifir ? 'Dengede' : pozitif ? 'Borç' : 'Alacak';
                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                                <span style={{ fontWeight: 700, fontSize: 13, color: renk, letterSpacing: '-0.01em' }}>
                                                    {Math.abs(bakiye).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                                                </span>
                                                <span style={{ fontSize: 10.5, fontWeight: 600, color: renk, opacity: 0.75 }}>{etiket}</span>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className={styles.colOdeme} style={{ textAlign: 'center' }}>
                                    {(() => {
                                        const status = odemeMap[musteri.cariID];
                                        if (!status) return <span style={{ color: '#d1d5db' }}>—</span>;
                                        const cfg = {
                                            red:    { cls: 'bg-red-50 text-red-700', label: t('durum.geciken') },
                                            yellow: { cls: 'bg-amber-50 text-amber-700', label: t('durum.yaklasan') },
                                            green:  { cls: 'bg-green-50 text-green-700', label: t('durum.duzenli') },
                                        }[status];
                                        return (
                                            <span className={`${cfg.cls} text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap`}>
                                                {cfg.label}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className={styles.colDurum}>
                                    {musteri.aktifMi ? (
                                        <span className={styles.badgeAktif}><span className={styles.dot + " " + styles.dotGreen}></span>{t('durum.aktif')}</span>
                                    ) : (
                                        <span className={styles.badgePasif}><span className={styles.dot + " " + styles.dotGray}></span>{t('durum.pasif')}</span>
                                    )}
                                </td>
                                <td className={styles.colIslem} style={{ textAlign: 'center' }}>
                                    <div className="flex items-center justify-center gap-0.5">
                                        <button
                                            onClick={(e) => handleEditClick(e, musteri.cariID)}
                                            style={{
                                                background: 'transparent', border: 'none',
                                                padding: '6px 8px', cursor: 'pointer',
                                                color: '#c4cdd6', fontSize: '13px',
                                                borderRadius: '6px', transition: 'color 0.15s, background 0.15s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.background = '#fff8f0'; }}
                                            onMouseLeave={e => { e.currentTarget.style.color = '#c4cdd6'; e.currentTarget.style.background = 'transparent'; }}
                                            title={t('btn.duzenle')}
                                        >
                                            <i className="fas fa-pen" style={{ fontSize: 11 }}></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredMüşteriler.length > 0 && (
                    <div className={styles.tableFooter}>
                        <div className={styles.pageSizeWrap}>
                            <span>{t('label.sayfaBasi')}</span>
                            <select
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                                className={styles.pageSizeSelect}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                        <span className={styles.pageInfo}>
                            {Math.min((currentPage - 1) * pageSize + 1, filteredMüşteriler.length)}–{Math.min(currentPage * pageSize, filteredMüşteriler.length)} / {filteredMüşteriler.length} kayıt
                        </span>
                        <div className={styles.paginationWrap}>
                            <button className={styles.pageBtn} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    className={styles.pageBtn + (currentPage === p ? ' ' + styles.pageBtnActive : '')}
                                    onClick={() => setCurrentPage(p)}
                                >{p}</button>
                            ))}
                            <button className={styles.pageBtn} disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                        </div>
                    </div>
                )}
            </div>
            {/* Düzenleme Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('musteri.duzenle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.adSoyad')}</Form.Label>
                                    <Form.Control type="text" value={formData.adiSoyadi}
                                        onChange={(e) => setFormData({ ...formData, adiSoyadi: e.target.value })}
                                        required maxLength={100} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.firmaAdi')}</Form.Label>
                                    <Form.Control type="text" value={formData.firmaAdi || ''}
                                        onChange={(e) => setFormData({ ...formData, firmaAdi: e.target.value || null })}
                                        maxLength={100} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.telefon1')}</Form.Label>
                                    <Form.Control type="text" value={formatPhoneNumber(formData.telefon1)}
                                        onChange={(e) => setFormData({ ...formData, telefon1: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                        required maxLength={14} placeholder="(5XX)XXX-XX-XX" />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Telefon 2</Form.Label>
                                    <Form.Control type="text" value={formData.telefon2 ? formatPhoneNumber(formData.telefon2) : ''}
                                        onChange={(e) => setFormData({ ...formData, telefon2: e.target.value ? e.target.value.replace(/\D/g, '').slice(0, 10) : null })}
                                        maxLength={14} placeholder="(5XX)XXX-XX-XX" />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>E-Posta</Form.Label>
                                    <Form.Control type="email" value={formData.ePosta || ''}
                                        onChange={(e) => setFormData({ ...formData, ePosta: e.target.value || null })}
                                        maxLength={100} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>TC</Form.Label>
                                    <Form.Control type="text" value={formData.tc || ''}
                                        onChange={(e) => setFormData({ ...formData, tc: e.target.value || null })}
                                        maxLength={11} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Vergi Dairesi</Form.Label>
                                    <Form.Control type="text" value={formData.cariVD || ''}
                                        onChange={(e) => setFormData({ ...formData, cariVD: e.target.value || null })}
                                        maxLength={100} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Vergi No</Form.Label>
                                    <Form.Control type="text" value={formData.cariVN || ''}
                                        onChange={(e) => setFormData({ ...formData, cariVN: e.target.value || null })}
                                        maxLength={50} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tür</Form.Label>
                                    <Form.Select value={formData.turu}
                                        onChange={(e) => setFormData({ ...formData, turu: e.target.value })}>
                                        <option value="Müşteri">Müşteri</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Kaynak</Form.Label>
                                    <Form.Select value={formData.kaynak || ''}
                                        onChange={(e) => setFormData({ ...formData, kaynak: e.target.value || null })}>
                                        <option value="">{t('label.seciniz')}</option>
                                        {leadKaynaklar.filter((k: any) => k.aktifMi).map((k: any) => (
                                            <option key={k.id} value={k.ad}>{k.ad}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Check type="switch" id="edit-aktif-switch" label="Aktif"
                                        checked={formData.aktifMi}
                                        onChange={(e) => setFormData({ ...formData, aktifMi: e.target.checked })} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Adres</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={formData.adres || ''}
                                        onChange={(e) => setFormData({ ...formData, adres: e.target.value || null })}
                                        maxLength={500} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>İptal</Button>
                        <Button variant="primary" type="submit">Kaydet</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default Musteriler;